import { OperationResult, PlaybackEvent, Reason, } from '@js-sdk/playback';
import { EventEmitter } from '@spotify-internal/emitter';
import { HarmonyErrorSource } from '../enums/error_source';
import { PlaybackStateObserver } from './playback_state_observer';
export class HarmonyStreamer extends EventEmitter {
    constructor(options) {
        super();
        this._client = options.client;
        this._initialVolume = options.initialVolume;
        this._listPlayer = options.listPlayer;
        this._playerPromise = options.playerPromise;
        this._tpApiClient = options.tpApiClient;
        this._playbackStateObserver = new PlaybackStateObserver(this._tpApiClient);
        this._stopOnBackground = options.stopOnBackground;
        this._init();
    }
    _init() {
        this._client.addListeners({
            ["before_disconnect" /* CLIENT_BEFORE_DISCONNECT */]: this._onClientBeforeDisconnect.bind(this),
            ["before_offline_disconnect" /* CLIENT_BEFORE_OFFLINE_DISCONNECT */]: this._onClientBeforeOfflineDisconnect.bind(this),
        });
        const listPlayer = this._listPlayer;
        listPlayer.addListeners({
            [PlaybackEvent.LIST_PLAYER_CAPPED]: this._onCapped.bind(this),
            [PlaybackEvent.LIST_PLAYER_TRACK_ENDED]: this._onTrackEnded.bind(this),
            [PlaybackEvent.LIST_PLAYER_BEFORE_VOLUME_CHANGE]: this._onBeforeVolumeChange.bind(this),
            [PlaybackEvent.LIST_PLAYER_ERROR]: this._onError.bind(this, HarmonyErrorSource.PLAYBACK),
        });
        // This cast is needed in order to make emitter type checking work for
        // proxying.
        this.proxyEmitAll(listPlayer, {
            [PlaybackEvent.LIST_PLAYER_AUTOPLAY_FAILED]: "autoplay_failed" /* STREAMER_AUTOPLAY_FAILED */,
            [PlaybackEvent.LIST_PLAYER_TRACK_LOADED]: "track_loaded" /* STREAMER_TRACK_LOADED */,
            [PlaybackEvent.LIST_PLAYER_DURATION_CHANGED]: "duration_changed" /* STREAMER_DURATION_CHANGED */,
            [PlaybackEvent.LIST_PLAYER_POSITION_CHANGED]: "position_changed" /* STREAMER_POSITION_CHANGED */,
            [PlaybackEvent.LIST_PLAYER_PROGRESS]: "progress" /* STREAMER_PROGRESS */,
            [PlaybackEvent.LIST_PLAYER_BUFFERING_END]: "buffering_end" /* STREAMER_BUFFERING_END */,
            [PlaybackEvent.LIST_PLAYER_BUFFERING_START]: "buffering_start" /* STREAMER_BUFFERING_START */,
            [PlaybackEvent.LIST_PLAYER_MAX_LIST_ERRORS_REACHED]: "max_list_errors_reached" /* STREAMER_MAX_LIST_ERRORS_REACHED */,
            [PlaybackEvent.LIST_PLAYER_VIDEO_ELEMENT_APPENDED]: "video_element_appended" /* STREAMER_VIDEO_ELEMENT_APPENDED */,
            [PlaybackEvent.LIST_PLAYER_VIDEO_ELEMENT_REMOVED]: "video_element_removed" /* STREAMER_VIDEO_ELEMENT_REMOVED */,
            [PlaybackEvent.LIST_PLAYER_LIST_ENDED]: "context_ended" /* STREAMER_CONTEXT_ENDED */,
        });
        const tpApiClient = this._tpApiClient;
        tpApiClient.addListeners({
            ["logout" /* TP_API_LOGOUT */]: this._onTPAPILogout.bind(this),
            ["error" /* TP_API_ERROR */]: this._onError.bind(this, HarmonyErrorSource.TRACK_PLAYBACK),
        });
        this.proxyEmit(tpApiClient, "max_subscriptions_reached" /* TP_API_MAX_SUBSCRIPTIONS_REACHED */, "max_subscriptions_reached" /* STREAMER_MAX_SUBSCRIPTIONS_REACHED */);
        this.proxyEmit(tpApiClient, "deregistered" /* TP_API_DEREGISTERED */, "deregistered" /* STREAMER_DEREGISTERED */);
        this.proxyEmit(tpApiClient, "registered" /* TP_API_REGISTERED */, "registered" /* STREAMER_REGISTERED */);
        this.proxyEmit(tpApiClient, "registration_aborted" /* TP_API_REGISTRATION_ABORTED */, "registration_aborted" /* STREAMER_REGISTRATION_ABORTED */);
        this.proxyEmit(this._playbackStateObserver, "state_changed" /* PLAYBACK_STATE_OBSERVER_STATE_CHANGED */, "state_changed" /* STREAMER_STATE_CHANGED */);
        if (this._stopOnBackground && document.addEventListener) {
            document.addEventListener("visibilitychange" /* WINDOW_VISIBILITY_CHANGE */, () => {
                if (document.hidden) {
                    // We stop and emit an event if the stop was successful.
                    if (this.stop()) {
                        this.emitSync("stopped_on_background" /* STREAMER_STOPPED_ON_BACKGROUND */, null);
                    }
                }
            });
        }
        this._playerPromise.then((player) => {
            player.setVolume(this._initialVolume);
            this.emit("player_initialization_done" /* STREAMER_PLAYER_INITIALIZATION_DONE */, null);
        }, (e) => {
            this.emit("player_initialization_failed" /* STREAMER_PLAYER_INITIALIZATION_FAILED */, {
                reason: e.message,
                error: e,
            });
        });
    }
    /**
     * Method for converting an operation result to boolean.
     *
     * @param result - The ListPlayer operation result.
     * @return Returns true if the result succeeded.
     */
    _assertOperationSuccess(result) {
        return result === OperationResult.SUCCESS;
    }
    _onTrackEnded() {
        // We do not want to forward track data
        this.emit("track_ended" /* STREAMER_TRACK_ENDED */, null);
    }
    _onClientBeforeDisconnect(e) {
        e.data.awaitPromise(this.deregister().catch(() => {
            // Ignore any errors from deregistering that might prevent other waiters
            // from disconnecting.
        }));
    }
    _onClientBeforeOfflineDisconnect(e) {
        e.data.awaitPromise(this.pause().catch(() => {
            // Ignore any errors.
        }));
    }
    _onBeforeVolumeChange(e) {
        const ev = this.emitSync("before_volume_change" /* STREAMER_BEFORE_VOLUME_CHANGE */, {
            volume: e.data.volume,
        });
        if (ev.defaultPrevented) {
            e.preventDefault();
        }
    }
    _onCapped() {
        // We do not want to forward track data
        this.emit("playback_capped" /* STREAMER_PLAYBACK_CAPPED */, null);
    }
    _onError(source, e) {
        const { error } = e.data;
        if (!error) {
            return;
        }
        if (error &&
            // the next check is needed to cast to TrackPlayerAPIError
            'registration' in error &&
            error.registration &&
            source === HarmonyErrorSource.TRACK_PLAYBACK) {
            this.emit("registration_error" /* STREAMER_REGISTRATION_ERROR */, {
                error,
            });
        }
        this.emit("error" /* STREAMER_ERROR */, {
            source,
            error,
        });
        // We need to cast playback errors to HarmonyErrors because playback
        // does not guarantee `status` to be set, whereas Harmony does.
        this._client.notifyError(source, error);
    }
    _onTPAPILogout() {
        this.emit("logged_out" /* STREAMER_LOGGED_OUT */, null);
    }
    stop() {
        return this._tpApiClient.stop();
    }
    register() {
        return this._tpApiClient.register();
    }
    deregister() {
        return this._tpApiClient.deregister();
    }
    // Player Controls
    /**
     * Resume playback (start playing).
     *
     * @return A promise that will be resolved with the value `true` if the operation was successful.
     */
    resume() {
        return this._listPlayer.resume().then(this._assertOperationSuccess);
    }
    /**
     * Pause playback.
     *
     * @return A promise that will be resolved with the value `true` if the operation was successful.
     */
    pause() {
        return this._listPlayer.pause().then(this._assertOperationSuccess);
    }
    /**
     * Skip to the next track.
     *
     * @param reason - Reason for next track
     * @return A promise that will be resolved with the value `true` if the operation was successful.
     */
    nextTrack(reason = Reason.FORWARD_BUTTON) {
        return this._listPlayer.next(reason).then(this._assertOperationSuccess);
    }
    /**
     * Checks if the player can change track.
     *
     * @return A promise that will be resolved with a boolean indicating if the client is allowed to change track
     */
    canChangeTrack() {
        return this._listPlayer.canChangeTrack();
    }
    /**
     * Skip to the previous track.
     *
     * @param reason - Reason for the previous track
     * @return A promise that will be resolved with the value `true` if the operation was successful.
     */
    previousTrack(reason = Reason.BACK_BUTTON) {
        return this._listPlayer.previous(reason).then(this._assertOperationSuccess);
    }
    /**
     * Toggle between play / pause.
     *
     * Will pause if already playing, otherwise start playing.
     *
     * @return A promise that will be resolved with the value `true` if the operation was successful.
     */
    togglePlay() {
        return this._listPlayer.togglePlay().then(this._assertOperationSuccess);
    }
    /**
     * Change the volume of the media element.
     *
     * @param value - The new volume, a float betwen 0.0 (silent) and 1.0 (loudest).
     * @return A promise that will be resolved with the value `true` if the operation was successful.
     */
    setVolume(value) {
        return this._listPlayer.setVolume(value).then(this._assertOperationSuccess);
    }
    /**
     * Get the current volume of the media element. 0.0 is silent, 1.0 is loudest.
     *
     * @return A promise that will be resolved with the volume.
     */
    getVolume() {
        return this._listPlayer.getVolume();
    }
    /**
     * Seek in the buffer.
     *
     * @param value - The new position (in milliseconds)
     * @return A promise that will be resolved with the value `true` if the operation was successful.
     */
    seek(value) {
        return this._listPlayer.seek(value).then(this._assertOperationSuccess);
    }
    /**
     * Get the current view of the playback state.
     *
     * @return A promise that resolves the current (or future, in case there is no track window yet)
     *     track window.
     */
    getCurrentState() {
        return this._playbackStateObserver.getCurrentState();
    }
    /**
     * Get the current list of video profiles.
     *
     * @return A promise that resolves a list of the current video profiles.
     */
    getVideoVariants() {
        return this._playerPromise.then((player) => {
            return player.getVideoVariants();
        });
    }
    /**
     * Overrides adaptive bitrate management, takes a property representing the
     * bitrate to use regardless of the current bandwidth.
     *
     * @param value - The highest possible bitrate to select.
     * @return A promise that will be resolved with the value `true` if the operation was successful.
     */
    setPreferredBitrate(value) {
        return this._playerPromise.then((player) => {
            return player
                .setPreferredBitrate(value)
                .then(this._assertOperationSuccess);
        });
    }
    activateElement() {
        return this._playerPromise.then((player) => player.activateElement());
    }
}
//# sourceMappingURL=streamer.js.map